import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BookmarkPlus, Copy, Eye, EyeOff, FileText, Loader2, Save, Trash2 } from "lucide-react";

const WEEKDAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtTime(hms) {
  const [h = "00", m = "00"] = (hms || "00:00:00").split(":");
  return `${h}:${m}`;
}

// Read-only mini 7-day grid of a week template's slots. Lets the operator
// review exactly what will overwrite a channel before they confirm apply.
function TemplateWeekPreview({ template }) {
  const byDay = React.useMemo(() => {
    const cols = Array.from({ length: 7 }, () => []);
    for (const it of template?.items || []) {
      const d = Number(it.day_of_week ?? 0);
      if (d >= 0 && d < 7) cols[d].push(it);
    }
    cols.forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return cols;
  }, [template]);

  const totalSlots = (template?.items || []).length;

  return (
    <div className="mt-2 rounded-sm border border-fuchsia-500/30 bg-black/30 p-2" data-testid={`template-week-preview-${template.id}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-fuchsia-300/80 mb-2 px-1">
        7-day preview · {totalSlots} slots — this is what will overwrite the target channel's recurring grid
      </div>
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAY_SHORT.map((label, d) => (
          <div key={d} className="min-w-0">
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground text-center border-b border-border/50 pb-1 mb-1">
              {label} <span className="text-fuchsia-400/70">{byDay[d].length}</span>
            </div>
            <div className="space-y-1 max-h-52 overflow-y-auto scrollbar-thin pr-0.5">
              {byDay[d].length === 0 ? (
                <div className="text-[9px] font-mono text-muted-foreground/50 text-center py-2">—</div>
              ) : (
                byDay[d].map((it, i) => (
                  <div
                    key={i}
                    className="rounded-[3px] px-1 py-1 border-l-2 bg-muted/30"
                    style={{ borderColor: it.color || "#3B82F6" }}
                    title={`${fmtTime(it.start_time)} · ${it.title || "Untitled"}`}
                    data-testid={`template-preview-slot-${template.id}-${d}-${i}`}
                  >
                    <div className="text-[9px] font-mono text-fuchsia-200/90 leading-tight">{fmtTime(it.start_time)}</div>
                    <div className="text-[9px] leading-tight truncate text-foreground/80">
                      {it.title || (it.category_id ? "Category" : it.asset_id ? "Asset" : "Slot")}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TemplatesDialog({ open, onClose, channelId, templates, initialTargetDate, canWrite = true, onChange, channels = [] }) {
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [sourceKind, setSourceKind] = useState("weekday"); // weekday | date | week
  const [sourceDow, setSourceDow] = useState("0");
  const [sourceDate, setSourceDate] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [applyingId, setApplyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [weekTarget, setWeekTarget] = useState({});
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(""); setReason(""); setSourceKind("weekday");
    setSourceDow("0"); setSourceDate(""); setSaving(false);
    setPreviewId(null);
    setTargetDate(initialTargetDate || "");
  }, [open, initialTargetDate]);

  const save = async () => {
    if (!name.trim()) { toast.error("Give this template a name"); return; }
    setSaving(true);
    try {
      const body = {
        channel_id: channelId,
        name: name.trim(),
        reason: reason || null,
        scope: sourceKind === "week" ? "week" : "day",
        source_day_of_week: sourceKind === "weekday" ? Number(sourceDow) : null,
        source_date: sourceKind === "date" ? sourceDate : null,
      };
      const r = await api.post("/schedule-templates", body);
      toast.success(`Saved template "${r.data.name}" · ${r.data.items?.length || 0} slots`);
      setName(""); setReason("");
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const applyOne = async (tpl) => {
    if (!targetDate) { toast.error("Pick a target date first"); return; }
    setApplyingId(tpl.id);
    try {
      const r = await api.post(`/schedule-templates/${tpl.id}/apply`, null, {
        params: { target_date: targetDate },
      });
      toast.success(`Applied "${tpl.name}" to ${targetDate} · ${r.data.applied} slots`);
      if (r.data.auto_published) {
        toast.info(`Publishing ${r.data.auto_published} asset${r.data.auto_published === 1 ? "" : "s"} to CDN…`, {
          description: "Today's tile will flip to Cloudflare edge in a few seconds",
        });
      }
      await onChange();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    }
    setApplyingId(null);
  };

  const applyWeekly = async (tpl) => {
    const target = weekTarget[tpl.id] || channelId;
    if (!target) { toast.error("Pick a target channel"); return; }
    const tname = channels.find((c) => c.id === target)?.name || "channel";
    if (!window.confirm(`Overwrite ${tname}'s entire weekly grid from "${tpl.name}"? This replaces its recurring schedule.`)) return;
    setApplyingId(tpl.id);
    try {
      const r = await api.post(`/schedule-templates/${tpl.id}/apply-weekly`, { target_channel_id: target });
      toast.success(`Applied "${tpl.name}" to ${tname} · ${r.data.applied} slots`);
      await onChange();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weekly apply failed");
    }
    setApplyingId(null);
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    setDeletingId(tpl.id);
    try {
      await api.delete(`/schedule-templates/${tpl.id}`);
      toast.success(`Deleted "${tpl.name}"`);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setDeletingId(null);
  };

  const channelTemplates = (templates || []).filter((t) => t.channel_id === channelId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="templates-dialog">
        <DialogHeader className="relative pb-4 mb-1">
          <div className="absolute inset-x-0 -top-6 -mx-6 h-1" style={{ background: "var(--brand-gradient)" }} />
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText className="w-5 h-5 text-fuchsia-400" strokeWidth={1.75} />
            Schedule templates
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Snapshot any day's programming as a reusable template (e.g. <b>Sports Weekend</b>, <b>Christmas Day</b>, <b>Black Friday</b>) and apply it to any future date in one click.
          </DialogDescription>
        </DialogHeader>

        {/* Read-only banner for roles without write access */}
        {!canWrite && (
          <div className="text-[11px] font-mono text-amber-500 border border-amber-500/40 bg-amber-500/10 rounded-sm px-3 py-2" data-testid="templates-readonly-banner">
            Your role has read-only access to templates. Save · Apply · Delete are disabled.
          </div>
        )}

        {/* Save new template */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title flex items-center gap-1.5">
              <BookmarkPlus className="w-3.5 h-3.5 text-fuchsia-400" /> Save current schedule as template
            </span>
          </div>
          <div className={`p-3 space-y-3 ${!canWrite ? "opacity-50 pointer-events-none" : ""}`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sports Weekend"
                  className="mt-1 rounded-sm"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason label (optional)</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Championship coverage"
                  className="mt-1 rounded-sm"
                  data-testid="template-reason-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Snapshot source</Label>
              <div className="mt-1 flex items-center gap-2">
                <div className="inline-flex rounded-sm border border-border overflow-hidden">
                  <button
                    onClick={() => setSourceKind("weekday")}
                    className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest ${sourceKind === "weekday" ? "bg-fuchsia-500/15 text-fuchsia-500" : "text-muted-foreground hover:bg-muted/40"}`}
                    data-testid="template-source-weekday"
                  >Weekday</button>
                  <button
                    onClick={() => setSourceKind("date")}
                    className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest ${sourceKind === "date" ? "bg-fuchsia-500/15 text-fuchsia-500" : "text-muted-foreground hover:bg-muted/40"}`}
                    data-testid="template-source-date"
                  >Specific date</button>
                  <button
                    onClick={() => setSourceKind("week")}
                    className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest border-l border-border ${sourceKind === "week" ? "bg-fuchsia-500/15 text-fuchsia-500" : "text-muted-foreground hover:bg-muted/40"}`}
                    data-testid="template-source-week"
                  >Full week</button>
                </div>
                {sourceKind === "weekday" ? (
                  <Select value={sourceDow} onValueChange={setSourceDow}>
                    <SelectTrigger className="w-48 h-9 rounded-sm bg-card border-border" data-testid="template-source-dow">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {WEEKDAY_LABELS.map((d, i) => (
                        <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : sourceKind === "date" ? (
                  <Input
                    type="date"
                    value={sourceDate}
                    onChange={(e) => setSourceDate(e.target.value)}
                    className="w-48 h-9 rounded-sm"
                    data-testid="template-source-date-input"
                  />
                ) : (
                  <span className="text-[11px] font-mono text-fuchsia-400/80">Snapshots all 7 recurring days</span>
                )}
                <Button
                  size="sm"
                  onClick={save}
                  disabled={saving || !channelId || !name.trim() || (sourceKind === "date" && !sourceDate)}
                  className="rounded-sm h-9 text-xs tenant-btn ml-auto"
                  data-testid="template-save-btn"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Save template
                </Button>
              </div>
              <div className="text-[10px] font-mono text-muted-foreground mt-1">
                {sourceKind === "weekday"
                  ? `Snapshots the weekly template for ${WEEKDAY_LABELS[Number(sourceDow)]}.`
                  : "Snapshots the exception rows on the chosen date (must have items on that date)."}
              </div>
            </div>
          </div>
        </div>

        {/* Existing templates */}
        <div className="panel">
          <div className="panel-header flex items-center justify-between">
            <span className="panel-title flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-fuchsia-400" />
              Saved templates ({channelTemplates.length})
            </span>
            <div className="flex items-center gap-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Apply to</Label>
              <Input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-40 h-8 rounded-sm text-xs"
                data-testid="template-target-date"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto scrollbar-thin">
            {channelTemplates.length === 0 ? (
              <div className="p-6 text-center text-xs font-mono text-muted-foreground">
                No templates yet for this channel. Save one above to reuse it on any future date.
              </div>
            ) : (
              channelTemplates.map((tpl) => (
                <div key={tpl.id} className="border-b border-border/50">
                <div
                  className="flex items-center justify-between px-3 py-2 hover:bg-muted/30"
                  data-testid={`template-row-${tpl.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-display font-bold truncate flex items-center gap-2">
                      {tpl.name}
                      <span className={`text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${tpl.scope === "week" ? "bg-fuchsia-500/20 text-fuchsia-300" : "bg-muted text-muted-foreground"}`}>
                        {tpl.scope === "week" ? "Full week" : "Day"}
                      </span>
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground truncate">
                      {(tpl.items?.length || 0)} slots
                      {tpl.reason ? ` · ${tpl.reason}` : ""}
                      {tpl.created_at ? ` · saved ${new Date(tpl.created_at).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    {tpl.scope === "week" ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPreviewId((p) => (p === tpl.id ? null : tpl.id))}
                          className={`rounded-sm h-7 text-[11px] border-fuchsia-500/40 ${previewId === tpl.id ? "bg-fuchsia-500/15 text-fuchsia-300" : "text-fuchsia-400 hover:bg-fuchsia-500/10"}`}
                          data-testid={`template-preview-toggle-${tpl.id}`}
                          title="Preview the 7-day grid before applying"
                        >
                          {previewId === tpl.id ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                          Preview
                        </Button>
                        <Select value={weekTarget[tpl.id] || channelId} onValueChange={(v) => setWeekTarget((p) => ({ ...p, [tpl.id]: v }))}>
                          <SelectTrigger className="w-40 h-7 rounded-sm bg-card border-border text-[11px]" data-testid={`template-week-target-${tpl.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            {channels.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          onClick={() => applyWeekly(tpl)}
                          disabled={applyingId === tpl.id || !canWrite}
                          className="rounded-sm h-7 text-[11px] tenant-btn"
                          data-testid={`template-apply-weekly-${tpl.id}`}
                        >
                          {applyingId === tpl.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Copy className="w-3 h-3 mr-1" />}
                          Apply week
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => applyOne(tpl)}
                        disabled={applyingId === tpl.id || !targetDate || !canWrite}
                        className="rounded-sm h-7 text-[11px] tenant-btn"
                        data-testid={`template-apply-${tpl.id}`}
                      >
                        {applyingId === tpl.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Copy className="w-3 h-3 mr-1" />}
                        Apply
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(tpl)}
                      disabled={deletingId === tpl.id || !canWrite}
                      className="rounded-sm h-7 w-7 p-0 border-rose-500/40 text-rose-500 hover:bg-rose-500/10"
                      data-testid={`template-delete-${tpl.id}`}
                      title={canWrite ? "Delete template" : "Read-only role"}
                    >
                      {deletingId === tpl.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
                {tpl.scope === "week" && previewId === tpl.id && (
                  <div className="px-3 pb-3">
                    <TemplateWeekPreview template={tpl} />
                  </div>
                )}
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm border-border">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
